import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Voice recorder button -> uploads to /api/assistant/transcribe
 * Props: onTranscribed(text), disabled, size ("sm"|"md")
 */
export default function VoiceRecorder({ onTranscribed, disabled = false, size = "md" }) {
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
  }, []);

  const start = async () => {
    if (disabled || loading) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) { toast.error("Enregistrement trop court"); return; }
        setLoading(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "dictee.webm");
          const { data } = await api.post("/assistant/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data?.text) onTranscribed?.(data.text);
          else toast.error("Transcription vide");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Erreur transcription");
        } finally { setLoading(false); }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error("Micro refusé par le navigateur");
    }
  };

  const stop = () => {
    try { recorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const sz = size === "sm" ? "h-9 w-9" : "h-10 w-10";
  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || loading}
      data-testid="ai-voice-btn"
      title={recording ? "Arrêter l'enregistrement" : "Dicter"}
      className={`${sz} rounded-md flex items-center justify-center border transition-colors ${recording ? "bg-red-500 text-white border-red-500 animate-pulse" : "bg-white text-slate-700 border-slate-200 hover:border-[#C5A059] hover:text-[#A88846]"}`}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
